// src/components/AppInformePedidosAgrupadoReactTable.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getReporteVentas } from "../helpers/rutaVentas";
import ridersLogo from "../images/ridersmiami-logo.png";

const clean = (v) => String(v ?? "").trim();

const normalizarTexto = (valor = "") =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const compararTexto = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "es", {
    numeric: true,
    sensitivity: "base",
  });

const obtenerZonaLogisticaPorProvincia = (provincia = "") => {
  const p = normalizarTexto(provincia);

  const mapa = {
    "LA HABANA": "PUERTO DEL MARIEL",
    "PINAR DEL RIO": "PUERTO DEL MARIEL",
    ARTEMISA: "PUERTO DEL MARIEL",
    MAYABEQUE: "PUERTO DEL MARIEL",
    MATANZAS: "PUERTO DEL MARIEL",

    CIENFUEGOS: "ZONA CENTRAL",
    "VILLA CLARA": "ZONA CENTRAL",
    "SANCTI SPIRITUS": "ZONA CENTRAL",
    "CIEGO DE AVILA": "ZONA CENTRAL",
    CAMAGUEY: "ZONA CENTRAL",
    "LAS TUNAS": "ZONA CENTRAL",

    "SANTIAGO DE CUBA": "SANTIAGO DE CUBA",
    GRANMA: "SANTIAGO DE CUBA",
    HOLGUIN: "SANTIAGO DE CUBA",
    GUANTANAMO: "SANTIAGO DE CUBA",
  };

  return mapa[p] || "";
};

const fechaInputHoy = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const fechaInputInicioMes = () => {
  const d = new Date();
  const inicio = new Date(d.getFullYear(), d.getMonth(), 1);
  return inicio.toISOString().slice(0, 10);
};

const formatoFecha = (fecha) => {
  if (!fecha) return "";

  const d = new Date(fecha);

  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const armarNombreReceptor = (receptor = {}) => {
  const nombreCompleto = clean(receptor.nombreCompleto);

  if (nombreCompleto) return nombreCompleto;

  return [
    clean(receptor.nombre),
    clean(receptor.segundoNombre),
    clean(receptor.apellido1),
    clean(receptor.apellido2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const armarNombreClienteCompra = (cliente = {}) => {
  const nombreCompleto = clean(cliente.nombreCompleto);

  if (nombreCompleto) return nombreCompleto;

  const nombreArmado = [
    clean(cliente.nombre),
    clean(cliente.apellido1 || cliente.apellido),
    clean(cliente.apellido2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return nombreArmado || clean(cliente.razonsocial) || "";
};

const armarDireccionCompleta = (receptor = {}) => {
  const direccionCompleta = clean(receptor.direccionCompleta);

  if (direccionCompleta) return direccionCompleta;

  return [
    clean(receptor.direccion),
    clean(receptor.referenciaDireccion),
    clean(receptor.municipio),
    clean(receptor.provincia),
    clean(receptor.pais) || "Cuba",
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizarFilaPedido = (fila = {}, index = 0) => {
  const receptor =
    fila.clienteRecibe && typeof fila.clienteRecibe === "object"
      ? fila.clienteRecibe
      : {};

  const clienteCompra =
    fila.clienteCompra && typeof fila.clienteCompra === "object"
      ? fila.clienteCompra
      : fila.cliente && typeof fila.cliente === "object"
      ? fila.cliente
      : {};

  const articulo =
    normalizarTexto(fila.descripcionProducto) ||
    normalizarTexto(fila.modeloProducto) ||
    normalizarTexto(fila.producto?.descripcion) ||
    normalizarTexto(fila.producto?.modelo) ||
    "";

  const provincia = normalizarTexto(receptor.provincia);
  const municipio = normalizarTexto(receptor.municipio);

  const zonaLogistica =
    normalizarTexto(receptor.zonaLogistica) ||
    obtenerZonaLogisticaPorProvincia(receptor.provincia);

  const nroventaTexto = clean(fila.nroventa);
  const trackReal = clean(fila.track);

  const track =
    trackReal || (nroventaTexto ? `ETR-${nroventaTexto.padStart(6, "0")}` : "");

  return {
    id: `${clean(fila.ventaId) || "venta"}-${clean(fila.itemId) || index}`,

    ventaId: clean(fila.ventaId),
    itemId: clean(fila.itemId),

    nroventa: nroventaTexto,
    track,
    trackReal,

    fecha: fila.fecha || "",
    fechaTexto: formatoFecha(fila.fecha),

    articulo: articulo || "SIN PRODUCTO",
    cantidad: num(fila.cantidad),

    clienteUSA: armarNombreClienteCompra(clienteCompra),

    nombre: armarNombreReceptor(receptor),
    identificacion: clean(receptor.carnetIdentidad),

    provincia: provincia || "SIN PROVINCIA",
    municipio: municipio || "SIN MUNICIPIO",
    zonaLogistica: zonaLogistica || "SIN ZONA",

    repartoBarrioPoblado:
      clean(receptor.repartoBarrioPoblado) ||
      clean(receptor.barrio) ||
      clean(receptor.reparto) ||
      clean(receptor.referenciaDireccion),

    direccionCompleta: armarDireccionCompleta(receptor),
    telefono: clean(receptor.telefonoPrincipal),

    estadoVenta: clean(fila.estadoVenta),
    estadoItem: clean(fila.estadoItem),
  };
};

const getOpciones = (filas, campo) => {
  const set = new Set();

  filas.forEach((fila) => {
    const valor = clean(fila[campo]);
    if (valor) set.add(valor);
  });

  return Array.from(set).sort(compararTexto);
};

const agruparTotales = (filas, campo) => {
  const mapa = new Map();

  filas.forEach((fila) => {
    const key = clean(fila[campo]) || "SIN DATO";
    const actual = mapa.get(key) || {
      nombre: key,
      pedidos: 0,
      unidades: 0,
    };

    actual.pedidos += 1;
    actual.unidades += num(fila.cantidad);

    mapa.set(key, actual);
  });

  return Array.from(mapa.values()).sort((a, b) => {
    if (b.unidades !== a.unidades) return b.unidades - a.unidades;
    return compararTexto(a.nombre, b.nombre);
  });
};

const AGRUPACIONES = {
  PROVINCIA_PRODUCTO: {
    label: "Provincia → Producto",
    labelPdf: "Provincia - Producto",
    campos: ["provincia", "articulo"],
    columnas: ["Provincia", "Producto"],
    color: "success",
  },
  PRODUCTO_PROVINCIA: {
    label: "Producto → Provincia",
    labelPdf: "Producto - Provincia",
    campos: ["articulo", "provincia"],
    columnas: ["Producto", "Provincia"],
    color: "success",
  },
  PROVINCIA_MUNICIPIO_PRODUCTO: {
    label: "Provincia → Municipio → Producto",
    labelPdf: "Provincia - Municipio - Producto",
    campos: ["provincia", "municipio", "articulo"],
    columnas: ["Provincia", "Municipio", "Producto"],
    color: "primary",
  },
  PRODUCTO_PROVINCIA_MUNICIPIO: {
    label: "Producto → Provincia → Municipio",
    labelPdf: "Producto - Provincia - Municipio",
    campos: ["articulo", "provincia", "municipio"],
    columnas: ["Producto", "Provincia", "Municipio"],
    color: "primary",
  },
};

const agruparNiveles = (filas, campos) => {
  const crearNodo = (nombre) => ({
    nombre,
    pedidos: 0,
    unidades: 0,
    hijos: new Map(),
  });

  const raiz = new Map();

  filas.forEach((fila) => {
    const cantidad = num(fila.cantidad) || 1;
    let mapaActual = raiz;

    campos.forEach((campo) => {
      const valor = clean(fila[campo]) || "SIN DATO";

      if (!mapaActual.has(valor)) {
        mapaActual.set(valor, crearNodo(valor));
      }

      const nodo = mapaActual.get(valor);

      nodo.pedidos += 1;
      nodo.unidades += cantidad;

      mapaActual = nodo.hijos;
    });
  });

  const convertir = (mapa) =>
    Array.from(mapa.values())
      .sort((a, b) => compararTexto(a.nombre, b.nombre))
      .map((nodo) => ({
        nombre: nodo.nombre,
        pedidos: nodo.pedidos,
        unidades: nodo.unidades,
        hijos: convertir(nodo.hijos),
      }));

  return convertir(raiz);
};

const obtenerFilasHojasAgrupadas = (
  nodos,
  cantidadColumnas,
  camino = [],
  out = []
) => {
  nodos.forEach((nodo) => {
    const nuevoCamino = [...camino, nodo.nombre];

    if (Array.isArray(nodo.hijos) && nodo.hijos.length > 0) {
      obtenerFilasHojasAgrupadas(
        nodo.hijos,
        cantidadColumnas,
        nuevoCamino,
        out
      );
    } else {
      const columnas = Array(cantidadColumnas).fill("");

      nuevoCamino.forEach((valor, idx) => {
        if (idx < cantidadColumnas) columnas[idx] = valor;
      });

      out.push([...columnas, nodo.pedidos, nodo.unidades]);
    }
  });

  return out;
};

const obtenerFilasPDFAgrupadas = (
  nodos,
  cantidadColumnas,
  nivelActual = 0,
  out = []
) => {
  nodos.forEach((nodo) => {
    const columnas = Array(cantidadColumnas).fill("");
    columnas[nivelActual] = nodo.nombre;

    out.push({
      row: [...columnas, String(nodo.pedidos), String(nodo.unidades)],
      nivel: nivelActual + 1,
    });

    if (Array.isArray(nodo.hijos) && nodo.hijos.length > 0) {
      obtenerFilasPDFAgrupadas(
        nodo.hijos,
        cantidadColumnas,
        nivelActual + 1,
        out
      );
    }
  });

  return out;
};

const exportarCSVAgrupado = ({
  filas,
  agrupacion,
  desde,
  hasta,
  totales,
}) => {
  const config = AGRUPACIONES[agrupacion];

  const headers = [
    ...config.columnas,

    "Fecha pedido cliente",
    "Track",
    "Producto",
    "Cantidad",
    "Cliente USA",
    "Nombre receptor",
    "Identificación",
    "Teléfono",
    "Provincia",
    "Municipio",
    "Zona logística",
    "Reparto/Barrio/Poblado",
    "Dirección completa",
    "Nro venta",
    "Estado venta",
    "Estado item",
  ];

  const values = filas.map((fila) => [
    ...config.campos.map((campo) => clean(fila[campo]) || "SIN DATO"),

    fila.fechaTexto || "",
    fila.track || "",
    fila.articulo || "",
    fila.cantidad || 1,
    fila.clienteUSA || "",
    fila.nombre || "",
    fila.identificacion || "",
    fila.telefono || "",
    fila.provincia || "",
    fila.municipio || "",
    fila.zonaLogistica || "",
    fila.repartoBarrioPoblado || "",
    fila.direccionCompleta || "",
    fila.nroventa || "",
    fila.estadoVenta || "",
    fila.estadoItem || "",
  ]);

  const escapar = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  const csv = [
    [`Informe de Pedidos Agrupado con Receptores`].map(escapar).join(";"),
    [`Desde`, desde || "-", `Hasta`, hasta || "-"].map(escapar).join(";"),
    [`Agrupación`, config.labelPdf || config.label].map(escapar).join(";"),
    [
      `Pedidos`,
      totales.totalPedidos,
      `Unidades`,
      totales.totalUnidades,
      `Provincias`,
      totales.totalProvincias,
      `Zonas`,
      totales.totalZonas,
    ]
      .map(escapar)
      .join(";"),
    "",
    headers.map(escapar).join(";"),
    ...values.map((row) => row.map(escapar).join(";")),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `informe-pedidos-receptores-${fechaInputHoy()}.csv`;
  a.click();

  URL.revokeObjectURL(url);
};

const exportarPDFAgrupado = ({
  datosAgrupados,
  agrupacion,
  desde,
  hasta,
  totales,
}) => {
  const config = AGRUPACIONES[agrupacion];
  const cantidadColumnas = config.columnas.length;
  const idxPedidos = cantidadColumnas;
  const idxUnidades = cantidadColumnas + 1;

  const doc = new jsPDF("landscape", "mm", "a4");

  doc.setFontSize(14);
  doc.text("Informe de Pedidos Agrupado", 10, 12);

  doc.setFontSize(9);
  doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 10, 19);
  doc.text(`Agrupación: ${config.labelPdf || config.label}`, 10, 25);
  doc.text(
    `Pedidos: ${totales.totalPedidos}   Unidades: ${totales.totalUnidades}   Provincias: ${totales.totalProvincias}   Zonas: ${totales.totalZonas}`,
    10,
    31
  );

  const bodyInfo = obtenerFilasPDFAgrupadas(datosAgrupados, cantidadColumnas);
  const body = bodyInfo.map((x) => x.row);

  autoTable(doc, {
    startY: 38,
    head: [[...config.columnas, "Pedidos", "Unidades"]],
    body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 1.6,
      overflow: "linebreak",
    },
    headStyles: {
      fontSize: 8,
    },
    columnStyles: {
      [idxPedidos]: { cellWidth: 22, halign: "right" },
      [idxUnidades]: { cellWidth: 24, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;

      const info = bodyInfo[data.row.index];
      if (!info) return;

      if (info.nivel === 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [235, 242, 255];
      }

      if (info.nivel === 2) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [245, 245, 245];
      }
    },
    margin: {
      left: 5,
      right: 5,
    },
  });

  doc.save(`informe-pedidos-agrupado-${fechaInputHoy()}.pdf`);
};

const exportarPDFAgrupadoConReceptores = async ({
  filas,
  agrupacion,
  desde,
  hasta,
  totales,
}) => {
  const config = AGRUPACIONES[agrupacion];
  const doc = new jsPDF("landscape", "mm", "a4");

  try {
    const img = new Image();

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = ridersLogo;
    });

    doc.addImage(img, "PNG", 8, 5, 28, 14);
  } catch (e) {
    console.warn("No se pudo cargar logo Riders:", e);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Informe de Pedidos con Receptores", 42, 11);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 42, 17);
  doc.text(`Agrupación: ${config.labelPdf || config.label}`, 42, 22);

  doc.text(
    `Pedidos: ${totales.totalPedidos}   Unidades: ${totales.totalUnidades}   Provincias: ${totales.totalProvincias}   Zonas: ${totales.totalZonas}`,
    42,
    27
  );

  const camposGrupo = config.campos || [];
  const columnasGrupo = config.columnas || [];

  const valorGrupo = (fila, campo) => clean(fila?.[campo]) || "SIN DATO";

  const totalizarGrupo = (nivel, filaActual) => {
    const camposPath = camposGrupo.slice(0, nivel + 1);

    const filasGrupo = filas.filter((fila) =>
      camposPath.every(
        (campo) => valorGrupo(fila, campo) === valorGrupo(filaActual, campo)
      )
    );

    return {
      pedidos: filasGrupo.length,
      unidades: filasGrupo.reduce(
        (acc, fila) => acc + (Number(fila.cantidad) || 1),
        0
      ),
    };
  };

  const body = [];
  const ultimosPaths = {};

  filas.forEach((fila) => {
    camposGrupo.forEach((campo, nivel) => {
      const path = camposGrupo
        .slice(0, nivel + 1)
        .map((c) => `${c}:${valorGrupo(fila, c)}`)
        .join("|");

      if (ultimosPaths[nivel] !== path) {
        for (let i = nivel; i < camposGrupo.length; i += 1) {
          ultimosPaths[i] = null;
        }

        ultimosPaths[nivel] = path;

        const tituloCampo = columnasGrupo[nivel] || campo;
        const valor = valorGrupo(fila, campo);
        const totalGrupo = totalizarGrupo(nivel, fila);

        body.push([
          {
            content: `${"   ".repeat(nivel)}${tituloCampo}: ${valor}   |   Pedidos: ${
              totalGrupo.pedidos
            }   |   Unidades: ${totalGrupo.unidades}`,
            colSpan: 11,
            styles: {
              fontStyle: "bold",
              fontSize: nivel === 0 ? 7 : 6.5,
              textColor: nivel === 0 ? [0, 0, 0] : [40, 40, 40],
              fillColor:
                nivel === 0
                  ? [225, 235, 250]
                  : nivel === 1
                  ? [240, 240, 240]
                  : [248, 248, 248],
              cellPadding: 1.4,
            },
          },
        ]);
      }
    });

    body.push([
      fila.fechaTexto || "-",
      fila.track || "-",
      fila.articulo || "-",
      fila.cantidad || 1,
      fila.nombre || "-",
      fila.identificacion || "-",
      fila.telefono || "-",
      fila.provincia || "-",
      fila.municipio || "-",
      fila.zonaLogistica || "-",
      fila.direccionCompleta || "-",
    ]);
  });

  autoTable(doc, {
    startY: 33,
    head: [
      [
        "Fecha",
        "Track",
        "Producto",
        "Cant.",
        "Receptor",
        "ID",
        "Teléfono",
        "Provincia",
        "Municipio",
        "Zona",
        "Domicilio",
      ],
    ],
    body,
    theme: "grid",
    styles: {
      fontSize: 5.4,
      cellPadding: 0.9,
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      fillColor: [220, 0, 0],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 5.7,
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 14 },
      1: { cellWidth: 18 },
      2: { cellWidth: 27 },
      3: { cellWidth: 8, halign: "right" },
      4: { cellWidth: 33 },
      5: { cellWidth: 22 },
      6: { cellWidth: 22 },
      7: { cellWidth: 20 },
      8: { cellWidth: 24 },
      9: { cellWidth: 25 },
      10: { cellWidth: 61 },
    },
    margin: {
      left: 5,
      right: 5,
    },
  });

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(`Página ${i} de ${pageCount}`, 285, 202, {
      align: "right",
    });
    doc.setTextColor(0);
  }

  doc.save(`informe-pedidos-receptores-agrupado-${fechaInputHoy()}.pdf`);
};

function ResumenCard({ titulo, valor, detalle }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Body>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {titulo}
        </div>

        <div style={{ fontSize: 24, fontWeight: 700 }}>{valor}</div>

        {!!detalle && (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {detalle}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function TablaTotales({ titulo, filas }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Header>
        <b>{titulo}</b>
      </Card.Header>

      <Card.Body style={{ padding: 0 }}>
        <Table size="sm" bordered hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Concepto</th>
              <th className="text-end">Pedidos</th>
              <th className="text-end">Unidades</th>
            </tr>
          </thead>

          <tbody>
            {!filas.length && (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  Sin datos
                </td>
              </tr>
            )}

            {filas.slice(0, 8).map((fila) => (
              <tr key={fila.nombre}>
                <td>{fila.nombre}</td>
                <td className="text-end">{fila.pedidos}</td>
                <td className="text-end">{fila.unidades}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function TablaAgrupada({ datosAgrupados, agrupacion, totales }) {
  const config = AGRUPACIONES[agrupacion];

  const renderNodos = (nodos, nivel = 1, prefijo = "") =>
    nodos.map((nodo, idx) => {
      const key = `${prefijo}${nodo.nombre}-${idx}`;
      const tieneHijos = Array.isArray(nodo.hijos) && nodo.hijos.length > 0;

      const estiloFila =
        nivel === 1
          ? { background: "#eef4ff", fontWeight: 700 }
          : nivel === 2
          ? { background: "#f8f9fa", fontWeight: 700 }
          : {};

      const paddingLeft = 12 + (nivel - 1) * 24;
      const icono = tieneHijos ? "▾" : "□";

      return (
        <React.Fragment key={key}>
          <tr style={estiloFila}>
            <td style={{ paddingLeft }}>
              {icono} {nodo.nombre}
            </td>
            <td className="text-end">{nodo.pedidos}</td>
            <td className="text-end">{nodo.unidades}</td>
          </tr>

          {tieneHijos && renderNodos(nodo.hijos, nivel + 1, `${key}-`)}
        </React.Fragment>
      );
    });

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <b>Resultado agrupado</b>{" "}
        <span className="text-muted">({config.label})</span>
      </Card.Header>

      <Card.Body style={{ padding: 0 }}>
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover size="sm" responsive className="align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "70%" }}>Concepto</th>
                <th className="text-end">Pedidos</th>
                <th className="text-end">Unidades</th>
              </tr>
            </thead>

            <tbody>
              {!datosAgrupados.length && (
                <tr>
                  <td colSpan="3" className="text-center text-muted py-4">
                    No hay datos para mostrar.
                  </td>
                </tr>
              )}

              {renderNodos(datosAgrupados)}
            </tbody>

            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td>TOTAL</td>
                <td className="text-end">{totales.totalPedidos}</td>
                <td className="text-end">{totales.totalUnidades}</td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function AppInformePedidosAgrupadoReactTable() {
  const [desde, setDesde] = useState(fechaInputInicioMes());
  const [hasta, setHasta] = useState(fechaInputHoy());

  const [q, setQ] = useState("");
  const [provincia, setProvincia] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [zonaLogistica, setZonaLogistica] = useState("");
  const [producto, setProducto] = useState("");

  const [agrupacion, setAgrupacion] = useState(
    "PROVINCIA_MUNICIPIO_PRODUCTO"
  );

  const [filasBase, setFilasBase] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getReporteVentas({
        desde,
        hasta,
      });

      const filasApi = Array.isArray(data?.filas) ? data.filas : [];

      const normalizadas = filasApi.map((fila, index) =>
        normalizarFilaPedido(fila, index)
      );

      setFilasBase(normalizadas);
    } catch (err) {
      console.error("❌ Error al cargar informe agrupado:", err);

      const mensaje =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "No se pudo cargar el informe agrupado.";

      setError(mensaje);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const provinciasOpts = useMemo(
    () => getOpciones(filasBase, "provincia"),
    [filasBase]
  );

  const municipiosOpts = useMemo(() => {
    const base = provincia
      ? filasBase.filter(
          (fila) => normalizarTexto(fila.provincia) === normalizarTexto(provincia)
        )
      : filasBase;

    return getOpciones(base, "municipio");
  }, [filasBase, provincia]);

  const zonasOpts = useMemo(
    () => getOpciones(filasBase, "zonaLogistica"),
    [filasBase]
  );

  const productosOpts = useMemo(
    () => getOpciones(filasBase, "articulo"),
    [filasBase]
  );

  const filasFiltradas = useMemo(() => {
    const nq = normalizarTexto(q);
    const nProvincia = normalizarTexto(provincia);
    const nMunicipio = normalizarTexto(municipio);
    const nZona = normalizarTexto(zonaLogistica);
    const nProducto = normalizarTexto(producto);

    return filasBase.filter((fila) => {
      if (nProvincia && normalizarTexto(fila.provincia) !== nProvincia) {
        return false;
      }

      if (nMunicipio && normalizarTexto(fila.municipio) !== nMunicipio) {
        return false;
      }

      if (nZona && normalizarTexto(fila.zonaLogistica) !== nZona) {
        return false;
      }

      if (nProducto && normalizarTexto(fila.articulo) !== nProducto) {
        return false;
      }

      if (nq) {
        const textoBusqueda = normalizarTexto(
          [
            fila.nombre,
            fila.identificacion,
            fila.telefono,
            fila.track,
            fila.articulo,
            fila.provincia,
            fila.municipio,
            fila.zonaLogistica,
            fila.direccionCompleta,
            fila.nroventa,
          ].join(" ")
        );

        if (!textoBusqueda.includes(nq)) return false;
      }

      return true;
    });
  }, [filasBase, q, provincia, municipio, zonaLogistica, producto]);

  const filasOrdenadas = useMemo(() => {
    const config = AGRUPACIONES[agrupacion];
    const arr = [...filasFiltradas];

    arr.sort((a, b) => {
      for (const campo of config.campos) {
        const cmp = compararTexto(a[campo], b[campo]);
        if (cmp !== 0) return cmp;
      }

      return compararTexto(a.nombre, b.nombre);
    });

    return arr;
  }, [filasFiltradas, agrupacion]);

  const totales = useMemo(() => {
    const totalPedidos = filasOrdenadas.length;

    const totalUnidades = filasOrdenadas.reduce(
      (acc, fila) => acc + num(fila.cantidad),
      0
    );

    const sinTrack = filasOrdenadas.filter((fila) => !clean(fila.track)).length;

    const sinTelefono = filasOrdenadas.filter(
      (fila) => !clean(fila.telefono)
    ).length;

    const provincias = new Set(
      filasOrdenadas.map((fila) => clean(fila.provincia)).filter(Boolean)
    );

    const zonas = new Set(
      filasOrdenadas.map((fila) => clean(fila.zonaLogistica)).filter(Boolean)
    );

    return {
      totalPedidos,
      totalUnidades,
      sinTrack,
      sinTelefono,
      totalProvincias: provincias.size,
      totalZonas: zonas.size,
    };
  }, [filasOrdenadas]);

  const totalesProducto = useMemo(
    () => agruparTotales(filasOrdenadas, "articulo"),
    [filasOrdenadas]
  );

  const totalesProvincia = useMemo(
    () => agruparTotales(filasOrdenadas, "provincia"),
    [filasOrdenadas]
  );

  const totalesZona = useMemo(
    () => agruparTotales(filasOrdenadas, "zonaLogistica"),
    [filasOrdenadas]
  );

  const datosAgrupados = useMemo(() => {
    const config = AGRUPACIONES[agrupacion];
    return agruparNiveles(filasOrdenadas, config.campos);
  }, [filasOrdenadas, agrupacion]);

  const limpiarFiltros = () => {
    setQ("");
    setProvincia("");
    setMunicipio("");
    setZonaLogistica("");
    setProducto("");
  };

  const cambiarProvincia = (value) => {
    setProvincia(value);
    setMunicipio("");
  };

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h5 className="mb-0">Informe de Pedidos Agrupado</h5>
              <small className="text-muted">
                Vista logística agrupada por provincia, municipio y producto.
              </small>
            </div>

            <div className="d-flex gap-2 mt-2 mt-md-0">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={cargarDatos}
                disabled={loading}
              >
                {loading ? "Actualizando..." : "Actualizar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={() =>
                  exportarCSVAgrupado({
                    filas: filasOrdenadas,
                    agrupacion,
                    desde,
                    hasta,
                    totales,
                  })
                }
                disabled={!filasOrdenadas.length}
              >
                Exportar CSV/Excel
              </Button>

              <Button
                variant="outline-danger"
                size="sm"
                onClick={() =>
                  exportarPDFAgrupado({
                    datosAgrupados,
                    agrupacion,
                    desde,
                    hasta,
                    totales,
                  })
                }
                disabled={!filasOrdenadas.length}
              >
                Exportar PDF
              </Button>

              <Button
                variant="danger"
                size="sm"
                onClick={() =>
                  exportarPDFAgrupadoConReceptores({
                    filas: filasOrdenadas,
                    agrupacion,
                    desde,
                    hasta,
                    totales,
                  })
                }
                disabled={!filasOrdenadas.length}
              >
                PDF con receptor
              </Button>

            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Nombre, CI, teléfono, track..."
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Provincia</Form.Label>
                <Form.Select
                  value={provincia}
                  onChange={(e) => cambiarProvincia(e.target.value)}
                >
                  <option value="">Todas</option>
                  {provinciasOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Municipio</Form.Label>
                <Form.Select
                  value={municipio}
                  onChange={(e) => setMunicipio(e.target.value)}
                >
                  <option value="">Todos</option>
                  {municipiosOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Zona logística</Form.Label>
                <Form.Select
                  value={zonaLogistica}
                  onChange={(e) => setZonaLogistica(e.target.value)}
                >
                  <option value="">Todas</option>
                  {zonasOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>Producto</Form.Label>
                <Form.Select
                  value={producto}
                  onChange={(e) => setProducto(e.target.value)}
                >
                  <option value="">Todos</option>
                  {productosOpts.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex align-items-end">
              <div className="mb-3">
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={limpiarFiltros}
                >
                  Limpiar filtros
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="border rounded p-3 bg-light">
                <Row className="align-items-center g-3">
                  <Col md={3}>
                    <b>Vista del informe</b>
                    <div className="mt-2">
                      <ButtonGroup size="sm">
                        <Button variant="outline-secondary" disabled>
                          Detalle
                        </Button>
                        <Button variant="primary">Agrupado</Button>
                      </ButtonGroup>
                    </div>
                  </Col>

                  <Col md={6}>
                    <b>Agrupar por</b>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {Object.entries(AGRUPACIONES).map(([key, config]) => (
                        <Button
                          key={key}
                          size="sm"
                          variant={
                            agrupacion === key
                              ? config.color
                              : `outline-${config.color}`
                          }
                          onClick={() => setAgrupacion(key)}
                        >
                          {config.label}
                        </Button>
                      ))}
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Los filtros se aplican primero y luego se organiza el
                      resultado según la agrupación seleccionada.
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={2}>
              <ResumenCard titulo="Pedidos" valor={totales.totalPedidos} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Unidades" valor={totales.totalUnidades} />
            </Col>

            <Col md={2}>
              <ResumenCard
                titulo="Provincias"
                valor={totales.totalProvincias}
              />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Zonas" valor={totales.totalZonas} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Sin track" valor={totales.sinTrack} />
            </Col>

            <Col md={2}>
              <ResumenCard titulo="Sin teléfono" valor={totales.sinTelefono} />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <TablaTotales
                titulo="Totales por producto"
                filas={totalesProducto}
              />
            </Col>

            <Col md={4}>
              <TablaTotales
                titulo="Totales por provincia"
                filas={totalesProvincia}
              />
            </Col>

            <Col md={4}>
              <TablaTotales
                titulo="Totales por zona logística"
                filas={totalesZona}
              />
            </Col>
          </Row>

          {loading && (
            <div className="text-center my-4">
              <Spinner animation="border" size="sm" />{" "}
              <span className="ms-2">Cargando pedidos...</span>
            </div>
          )}

          {!loading && (
            <TablaAgrupada
              datosAgrupados={datosAgrupados}
              agrupacion={agrupacion}
              totales={totales}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
